import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { LOCALE_CONSTANTS } from './locale.constants';

declare const google: any;

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly isBrowser: boolean;
  private readonly localeSubject = new BehaviorSubject<string>(LOCALE_CONSTANTS.DEFAULT_LOCALE);

  readonly locale$ = this.localeSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      const savedLocale = this.getSavedLocale();
      this.localeSubject.next(savedLocale);

      if (savedLocale !== LOCALE_CONSTANTS.DEFAULT_LOCALE) {
        this.applyOnLoad(savedLocale);
      }
    }
  }

  /**
   * Waits for Google Translate to initialize, then applies the saved locale.
   */
  private applyOnLoad(langCode: string): void {
    this.setGoogTransCookie(langCode);

    const poll = setInterval(() => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
      if (combo) {
        clearInterval(poll);
        combo.value = langCode;
        combo.dispatchEvent(new Event('change'));
      }
    }, 500);

    setTimeout(() => clearInterval(poll), 10000);
  }

  get currentLocale(): string {
    return this.localeSubject.value;
  }

  /**
   * Sets the active locale:
   * 1. Persists to localStorage
   * 2. Sets the googtrans cookie
   * 3. Triggers Google Translate via the hidden combo box
   */
  setLocale(langCode: string): void {
    if (!this.isBrowser) return;

    const previousLocale = this.currentLocale;
    if (langCode === previousLocale) return;

    this.localeSubject.next(langCode);
    localStorage.setItem(LOCALE_CONSTANTS.STORAGE_KEY, langCode);

    if (langCode === LOCALE_CONSTANTS.DEFAULT_LOCALE) {
      this.clearGoogTransCookie();
    } else {
      this.setGoogTransCookie(langCode);
    }

    window.location.href = window.location.pathname + window.location.search;
  }

  /**
   * Resolves initial locale from: localStorage > browser language > default 'en'
   */
  private getSavedLocale(): string {
    const stored = localStorage.getItem(LOCALE_CONSTANTS.STORAGE_KEY);
    if (stored) return stored;

    const browserLang = this.getBrowserLanguage();
    const supported = LOCALE_CONSTANTS.SUPPORTED_LANGUAGES.find(
      (l) => l.code === browserLang || l.code === browserLang.split('-')[0]
    );
    return supported ? supported.code : LOCALE_CONSTANTS.DEFAULT_LOCALE;
  }

  private getBrowserLanguage(): string {
    if (!this.isBrowser) return LOCALE_CONSTANTS.DEFAULT_LOCALE;
    return navigator.language || (navigator as any).userLanguage || LOCALE_CONSTANTS.DEFAULT_LOCALE;
  }

  /**
   * Returns the root domain for cookie (e.g. '.topfan.dev' from 'www7.topfan.dev').
   */
  private getRootDomain(): string {
    const parts = window.location.hostname.split('.');
    if (parts.length <= 2) return window.location.hostname;
    return '.' + parts.slice(-2).join('.');
  }

  private setGoogTransCookie(langCode: string): void {
    const value = `/en/${langCode}`;
    const rootDomain = this.getRootDomain();
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=${value};path=/`;
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=${value};path=/;domain=${window.location.hostname}`;
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=${value};path=/;domain=${rootDomain}`;
  }

  private clearGoogTransCookie(): void {
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
    const rootDomain = this.getRootDomain();
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=;path=/;${expired}`;
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=;path=/;domain=${window.location.hostname};${expired}`;
    document.cookie = `${LOCALE_CONSTANTS.COOKIE_NAME}=;path=/;domain=${rootDomain};${expired}`;
  }

  /**
   * Drives Google Translate Element by programmatically changing its hidden <select>.
   */
  private triggerGoogleTranslate(langCode: string): void {
    try {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
      if (combo) {
        combo.value = langCode;
        combo.dispatchEvent(new Event('change'));
        return;
      }

      const waitForCombo = setInterval(() => {
        const el = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (el) {
          clearInterval(waitForCombo);
          el.value = langCode;
          el.dispatchEvent(new Event('change'));
        }
      }, 500);

      setTimeout(() => clearInterval(waitForCombo), 5000);
    } catch (_) {
      window.location.reload();
    }
  }

  /**
   * Re-applies the current locale after Angular route changes.
   * Call from app.component on NavigationEnd.
   */
  reapplyTranslation(): void {
    if (!this.isBrowser) return;
    const locale = this.currentLocale;
    if (locale && locale !== LOCALE_CONSTANTS.DEFAULT_LOCALE) {
      setTimeout(() => this.triggerGoogleTranslate(locale), 300);
    }
  }

  /**
   * Clears saved locale on logout, re-detects browser language,
   * and reloads if translation needs to change.
   */
  resetOnLogout(): void {
    if (!this.isBrowser) return;

    localStorage.removeItem(LOCALE_CONSTANTS.STORAGE_KEY);
    this.clearGoogTransCookie();

    const detectedLocale = this.detectBrowserLocale();
    const previousLocale = this.currentLocale;
    this.localeSubject.next(detectedLocale);

    if (detectedLocale !== LOCALE_CONSTANTS.DEFAULT_LOCALE) {
      localStorage.setItem(LOCALE_CONSTANTS.STORAGE_KEY, detectedLocale);
      this.setGoogTransCookie(detectedLocale);
    }

    if (previousLocale !== detectedLocale) {
      window.location.reload();
    }
  }

  private detectBrowserLocale(): string {
    const browserLang = this.getBrowserLanguage();
    const supported = LOCALE_CONSTANTS.SUPPORTED_LANGUAGES.find(
      (l) => l.code === browserLang || l.code === browserLang.split('-')[0]
    );
    return supported ? supported.code : LOCALE_CONSTANTS.DEFAULT_LOCALE;
  }
}
